// 1. React and core hooks
import { useCallback, useRef, useMemo } from 'react';

// 2. External libraries
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// 3. Internal components
import { WebSocketContext } from '~/contexts/WebSocketContext';

// 4. Type imports
import type { ReactNode } from 'react';
import type { ListTopicsResponse, TopicResponse } from '~/generated/api/model';
import type { AnyWebSocketEvent, ChangedEventData } from '~/types/websocket';
import type { WebSocketEventHandler } from '~/contexts/WebSocketContext';

// 5. Custom hooks
import { queryKeys } from '~/hooks/queryKeys';
import { useWebSocket } from '~/hooks/useWebSocket';

// 6. Utils
import { isDevelopment } from '~/utils/environment';

interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * Provider that manages global WebSocket connection and handles real-time events.
 *
 * Handles entity-based cache invalidation via the unified 'changed' event type:
 * - topics: Invalidates topics list query
 * - topic: Invalidates single topic query by id
 * - connections: Invalidates connections list for a topic
 * - connection: Invalidates connection query, cascades to list; shows toast if reason='disabled'
 * - messages: Invalidates messages query; optimistically updates topic counts if sequenceNumber present
 * - notifications: Invalidates notifications list and unread count
 * - oauth_clients: Invalidates OAuth apps and cascades to all connections
 *
 * Per websocket-notification-simplification-spec.md, events use a generic wrapper structure:
 * { type: 'changed', data: { entity, id?, topicId?, sequenceNumber?, reason? } }
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <QueryClientProvider client={queryClient}>
 *       <WebSocketProvider>
 *         <YourApp />
 *       </WebSocketProvider>
 *     </QueryClientProvider>
 *   );
 * }
 * ```
 */
export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const queryClient = useQueryClient();

  // Per §TS.5.2: Use ref to store subscribers without causing re-renders
  const subscribersRef = useRef<Set<WebSocketEventHandler>>(new Set());

  // Per §TS.13.1: Memoize subscription function (stable reference)
  const subscribe = useCallback((handler: WebSocketEventHandler) => {
    subscribersRef.current.add(handler);

    // Return unsubscribe function
    return () => {
      subscribersRef.current.delete(handler);
    };
  }, []);

  // Handle unified 'changed' event by routing to entity-specific invalidation
  const handleChangedEvent = useCallback(
    (data: ChangedEventData) => {
      const { entity, id, topicId, sequenceNumber, reason } = data;

      switch (entity) {
        case 'topics':
          void queryClient.invalidateQueries({ queryKey: queryKeys.topics.all });
          break;

        case 'topic':
          if (!id) {
            if (isDevelopment()) {
              throw new Error("Changed event 'topic' entity requires 'id' field");
            }
            console.warn("Changed event 'topic' entity missing required 'id' field");
            return;
          }
          void queryClient.invalidateQueries({ queryKey: queryKeys.topic(id) });
          break;

        case 'connections':
          if (!topicId) {
            if (isDevelopment()) {
              throw new Error("Changed event 'connections' entity requires 'topicId' field");
            }
            console.warn("Changed event 'connections' entity missing required 'topicId' field");
            return;
          }
          void queryClient.invalidateQueries({
            queryKey: queryKeys.connections.list(topicId),
            exact: false,
          });
          break;

        case 'connection':
          if (!topicId || !id) {
            if (isDevelopment()) {
              throw new Error(
                "Changed event 'connection' entity requires 'topicId' and 'id' fields"
              );
            }
            console.warn(
              "Changed event 'connection' entity missing required 'topicId' or 'id' field"
            );
            return;
          }
          void queryClient.invalidateQueries({ queryKey: queryKeys.connection(topicId, id) });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.connections.list(topicId),
            exact: false,
          });
          if (reason === 'disabled') {
            toast.error('A connection was disabled due to delivery failures');
          }
          break;

        case 'oauth_clients':
          void queryClient.invalidateQueries({ queryKey: queryKeys.authorizedApps });
          void queryClient.invalidateQueries({ queryKey: queryKeys.connections.all, exact: false });
          break;

        case 'messages':
          if (!topicId) {
            if (isDevelopment()) {
              throw new Error("Changed event 'messages' entity requires 'topicId' field");
            }
            console.warn("Changed event 'messages' entity missing required 'topicId' field");
            return;
          }
          void queryClient.invalidateQueries({ queryKey: queryKeys.topicMessages(topicId) });
          // Invalidate connections to update health metrics (messagesBehind is calculated server-side)
          void queryClient.invalidateQueries({
            queryKey: queryKeys.connections.list(topicId),
            exact: false,
          });

          // Optimistically update topic sequence if provided
          if (sequenceNumber !== undefined) {
            const updateTopicSequence = (old: TopicResponse | undefined) => {
              if (!old) return old;
              return {
                ...old,
                messageCount: (old.messageCount ?? 0) + 1,
                currentSequence: sequenceNumber,
              };
            };

            queryClient.setQueryData<TopicResponse>(queryKeys.topic(topicId), updateTopicSequence);
            queryClient.setQueriesData<ListTopicsResponse>(
              { queryKey: queryKeys.topics.all },
              (old) => {
                if (!old) return old;
                return {
                  ...old,
                  topics: old.topics.map((topic) =>
                    topic.id === topicId ? updateTopicSequence(topic)! : topic
                  ),
                };
              }
            );
          }
          break;

        case 'notifications':
          // Per §FE.4.1: notifications.all is a prefix of notifications.count,
          // so this single call transitively invalidates both list and count queries.
          void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
          break;

        default:
          console.warn(`Unknown changed event entity: ${entity as string}`);
      }
    },
    [queryClient]
  );

  // Per §TS.3.4: Memoize callbacks with explicit parameter types
  const handleMessage = useCallback(
    (wsEvent: AnyWebSocketEvent) => {
      // Dispatch to all subscribers first (per §BASIC.2: avoid duplication).
      // isolate each subscriber. A throwing subscriber must not block
      // the remaining subscribers or the global query-cache invalidation below.
      subscribersRef.current.forEach((handler) => {
        try {
          handler(wsEvent);
        } catch (error) {
          console.error('WebSocket subscriber handler threw; continuing dispatch', error);
        }
      });

      // Then handle global query cache updates
      switch (wsEvent.type) {
        case 'changed': {
          handleChangedEvent(wsEvent.data);
          break;
        }
        // Onboarding events are handled by subscribers only, no global cache updates needed
      }
    },
    [handleChangedEvent]
  );

  const { isConnected } = useWebSocket({
    onMessage: handleMessage,
  });

  // Per §TS.13.1: Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      isConnected,
      subscribe,
    }),
    [isConnected, subscribe]
  );

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
}
